import { type UserSession } from '@solinkcorp/authentication';
import { getAccessibleAddress } from '@solinkcorp/utils';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import { type VideoPlayerType } from 'src/core/video/VideoUtil';
import { type SelectedPlayListSource } from 'utils/types';
import { trackEvent } from '../analytics';
import { type AnalyticsPlatform } from '../analytics/common';
import { isMP4Available } from './supportedPlayers';
import { type SelectedStream } from './types';
export interface PlayerTrackingInfo {
  appDate: string;
  audioBitrate?: number;
  audioEnabled?: boolean;
  audioEncoding?: string;
  audioSamplingRate?: number;
  cameraBitrate?: number;
  cameraBrokenDuration?: boolean;
  cameraBrokenSei?: boolean;
  cameraBrokenTimestamps?: boolean;
  /** count of all playing cameras */
  cameraCount: number;
  cameraFirmware?: string;
  cameraHeight?: number;
  cameraId: string;
  cameraManufacturer?: string;
  cameraModel?: string;
  cameraName: string;
  cameraStreamName?: string;
  cameraWidth?: number;
  connectVersion?: string;
  local: boolean;
  locationName: string;
  locationId: string;
  siteId: string;
  deviceId: string;
  kbps: string;
  relayServer: string;
  source: string;
  playerInfo: string | undefined;
  playerType: VideoPlayerType | undefined;
  playingLive: boolean;
  type?: string;
  subType?: string;
  uploadSpeed?: number;
  url?: string;
  usePreflight: boolean;
  videoType?: string;
  platform?: AnalyticsPlatform;
  /** number of cameras used to calculate average load time and aggergate camera errors  */
  statsCameraCount?: number;
  /** aggregation intervals, in seconds, for video wall analytics events */
  aggregateIntervalSeconds?: number;
}

export interface MeasureVideoLoadTimeEvent extends PlayerTrackingInfo {
  loadTimeSec: number;
  maxLoadTimeSec?: number;
  minLoadTimeSec?: number;
  errors?: string[];
}

export interface PlaybackErrorEvent extends PlayerTrackingInfo {
  error: string;
  errors?: string[];
}
export interface PlayerTypeFallbackEvent extends PlayerTrackingInfo {
  error: string;
  fallbackPlayerType: VideoPlayerType;
}

const LOAD_VIDEO = {
  name: 'load-video',
  trackIntercom: true,
  trackMixpanel: true,
};

const MEASURED_VIDEO_LOAD_TIME = {
  name: 'measured-video-load-time',
  trackIntercom: true,
  trackMixpanel: true,
};

const VIDEO_PLAYBACK_ERROR = {
  name: 'video-playback-error',
  trackIntercom: true,
  trackMixpanel: true,
};

const PLAYER_TYPE_FALLBACK = {
  name: 'player-type-fallback',
  trackIntercom: true,
  trackMixpanel: true,
};

export const knownAndIgnoredErrors = [
  'The play() request was interrupted by a new load request.',
  'The play() request was interrupted because video-only background media was paused to save power.',
];

/**
 * Log analytics for loading video.
 */
export function loadVideo(properties: PlayerTrackingInfo): void {
  trackEvent({
    ...LOAD_VIDEO,
    properties: (properties as any) || {},
  });
}

export function measureVideoLoadTime(
  properties: PlayerTrackingInfo & { loadTimeSec: number },
): void {
  trackEvent({
    ...MEASURED_VIDEO_LOAD_TIME,
    properties: properties as any,
  });
}

export function videoPlaybackError(
  properties: PlayerTrackingInfo & { error: string; errors?: string[] },
): void {
  if (
    knownAndIgnoredErrors.some(
      (knownAndIgnoredError) =>
        typeof properties.error === 'string' && properties.error.includes(knownAndIgnoredError),
    )
  ) {
    // Ignore known and ignored errors
    return;
  }
  trackEvent({
    ...VIDEO_PLAYBACK_ERROR,
    properties: properties as any,
  });
}

/** track players falling back to fallbackPlayerType */
export function playerTypeFallback(
  properties: PlayerTrackingInfo & { error: string; fallbackPlayerType: VideoPlayerType },
): void {
  trackEvent({
    ...PLAYER_TYPE_FALLBACK,
    properties: properties as any,
  });
}

const VIDEO_SOURCE = {
  DIRECT: 'Direct',
  CLOUD_ONLY_USER: 'Cloud VMS (cloudOnly user)',
  USE_CLOUD_VMS: 'Cloud VMS (developer selected)',
  USE_RELAY_SERVER: 'Relay Server (developer selected)',
  RELAY_SERVER: 'Relay Server',
  CLOUD_VMS: 'Cloud VMS',
};

export function getVideoSourceForAnalytics({
  locationIsLocal,
  userIsCloudOnly,
  playingSource,
  devSelectedSource,
}: {
  locationIsLocal: boolean | undefined;
  userIsCloudOnly: boolean | undefined;
  playingSource?: 'cloud' | 'local';
  devSelectedSource: SelectedPlayListSource;
}): string {
  let source = VIDEO_SOURCE.RELAY_SERVER;

  if (locationIsLocal) {
    source = VIDEO_SOURCE.DIRECT;
  } else {
    if (playingSource === 'cloud') {
      const deviceOrUserSetToCloudOnly = userIsCloudOnly;

      if (deviceOrUserSetToCloudOnly) {
        source = VIDEO_SOURCE.CLOUD_ONLY_USER;
      } else if (devSelectedSource === 'cloudonly') {
        source = VIDEO_SOURCE.USE_CLOUD_VMS;
      } else {
        source = VIDEO_SOURCE.CLOUD_VMS;
      }
    } else if (devSelectedSource === 'localonly') {
      source = VIDEO_SOURCE.USE_RELAY_SERVER;
    }
  }

  return source;
}

/**
 * Parse the relay server name from the device's url
 */
function getRelayServerName(url: string, deviceId: string): string {
  // Relay 1 uses .solink.direct host
  const HOST_SOLINK_DIRECT = '.solink.direct';
  // Relay 2 uses .solinkcloud.com host
  const HOST_SOLINK_CLOUD = '.solinkcloud.com';
  const regexString = `${deviceId}.(.+)(${HOST_SOLINK_DIRECT}|${HOST_SOLINK_CLOUD})`;
  const regex = new RegExp(regexString, 'g');
  const matches = regex.exec(url);
  return matches ? matches[1] : '';
}

export function getMixpanelVideoTrackingInfo({
  appDate,
  cameraCount,
  playingCamera,
  playerInfo,
  playingLive,
  playerType,
  selectedStream,
  selectedPlaylistSource,
  user,
  platform,
  locationIsLocal,
}: {
  appDate: number;
  cameraCount: number;
  playingCamera: PlayingCamera;
  playerType: VideoPlayerType | undefined;
  playerInfo: string | undefined;
  playingLive: boolean;
  selectedStream: SelectedStream | undefined;
  selectedPlaylistSource: SelectedPlayListSource;
  user: UserSession;
  platform?: AnalyticsPlatform;
  locationIsLocal: boolean;
}): PlayerTrackingInfo {
  const NOT_AVAILABLE = 'N/A';

  const {
    camera: {
      audioParams,
      firmwareVersion: cameraFirmware,
      id: cameraId,
      manufacturer: cameraManufacturer,
      model: cameraModel,
      name: cameraName,
    },
    location: { id: locationId, name: locationName },
    device,
  } = playingCamera;

  const audioStats = selectedStream?.stream.audioStats;
  const average_bps = selectedStream?.stream.average_bps ?? 0;
  const dimensions = selectedStream?.stream.dimensions;
  const qa = selectedStream?.stream.qa;

  const audioEnabled = audioParams?.enabled && !!audioStats && audioStats.bitrate > 0;

  const source = getVideoSourceForAnalytics({
    locationIsLocal,
    userIsCloudOnly: user.jwtUser.app_metadata.cloudOnly || false,
    playingSource: selectedStream?.source,
    devSelectedSource: selectedPlaylistSource,
  });

  const url = getAccessibleAddress(playingCamera.camera, locationIsLocal) ?? undefined;

  const relayServer = locationIsLocal || !url ? 'Local' : getRelayServerName(url, locationId);

  const mp4Enabled = isMP4Available(user, selectedStream);

  const trackingInfo: PlayerTrackingInfo = {
    appDate: new Date(appDate).toISOString(),
    audioBitrate: audioStats?.bitrate,
    audioEnabled,
    audioEncoding: audioStats?.codec,
    audioSamplingRate: audioStats?.s_rate,
    cameraBitrate: average_bps,
    cameraBrokenDuration: qa?.brokenDuration,
    cameraBrokenSei: qa?.brokenSEI,
    cameraBrokenTimestamps: qa?.brokenTimestamps,
    cameraCount,
    cameraHeight: dimensions?.height,
    cameraId,
    cameraFirmware: cameraFirmware || NOT_AVAILABLE,
    cameraManufacturer: cameraManufacturer || NOT_AVAILABLE,
    cameraModel: cameraModel || NOT_AVAILABLE,
    cameraName,
    cameraStreamName: selectedStream?.stream.name,
    cameraWidth: dimensions?.width,
    kbps: `${Math.floor((average_bps ?? 0) / 1000)}`,
    local: !!locationIsLocal,
    locationId,
    siteId: locationId,
    deviceId: device.id,
    locationName,
    relayServer,
    connectVersion: device.currentSoftwareVersion,
    source,
    playerInfo,
    playerType,
    playingLive,
    type: getType(playerType, playingLive),
    subType: getSubType(playerType, mp4Enabled),
    uploadSpeed: device?.speedTestUploadRateS3,
    url,
    usePreflight: !user.thirdPartyCookiesSet,
    videoType: getVideoType(playerType, playingLive, mp4Enabled),
  };

  if (platform) {
    trackingInfo.platform = platform;
  }

  return trackingInfo;
}

/**
 * Translate newer player types to old "videoType" strings to keep our analytics consistent.
 */
function getVideoType(
  playerType: VideoPlayerType | undefined,
  playingLive: boolean,
  mp4Enabled: boolean,
): string | undefined {
  switch (playerType) {
    case 'HLS':
      if (playingLive) {
        return 'HLS TS Low Latency';
      } else if (mp4Enabled) {
        return 'HLS fMP';
      }
      return 'HLS';

    default:
      return playerType;
  }
}

/**
 * Translate newer player types to old "type" strings to keep our analytics consistent.
 */
function getType(
  playerType: VideoPlayerType | undefined,
  playingLive: boolean,
): string | undefined {
  switch (playerType) {
    case 'HLS':
      return playingLive ? 'Live' : 'Archived';
    default:
      return playerType;
  }
}

/**
 * Translate player types to old "subType" string to keep our analytics consistent.
 */
const getSubType = (
  playerType: VideoPlayerType | undefined,
  mp4Enabled: boolean,
): string | undefined => {
  switch (playerType) {
    case 'HLS':
      if (mp4Enabled) {
        return 'HLS mp4';
      }
      return undefined;

    default:
      return undefined;
  }
};
